import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { HERO_VIDEO, posterOf } from "@/data/studio";
import { Eyebrow, Pill, SEASE, scrollToId } from "./ui";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1.2, ease: SEASE, delay }}>{children}</motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const vid = useRef(null);
  const [muted, setMuted] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const toggle = () => {
    const v = vid.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play();
    setMuted(v.muted);
  };

  return (
    <section ref={ref} data-testid="studio-hero" className="relative flex min-h-[100svh] items-end overflow-hidden bg-sink">
      <motion.div className="absolute inset-0" initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: SEASE }}>
        <video ref={vid} src={HERO_VIDEO} poster={posterOf(HERO_VIDEO)} autoPlay muted loop playsInline onClick={toggle} data-cursor="play" data-testid="studio-hero-video"
          className="h-full w-full object-cover" />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-sink via-sink/55 to-sink/20" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-sink/80 via-sink/20 to-transparent" />

      <motion.div style={{ y, opacity: fade }} className="relative mx-auto w-full max-w-[1480px] px-5 pb-24 pt-40 sm:px-8 sm:pb-28 lg:px-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}>
          <Eyebrow>AI Video Production by Perfect Digitals</Eyebrow>
        </motion.div>
        <h1 data-testid="studio-hero-title" className="mt-6 max-w-5xl font-display text-[8.5vw] font-extrabold leading-[0.95] tracking-tight text-white sm:text-[6.5vw] xl:text-[78px]">
          <Line delay={0.35}>From AI Generation</Line>
          <Line delay={0.5}>to <span className="font-serif italic font-normal text-brand">Finished Production.</span></Line>
        </h1>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: SEASE }} className="mt-8 max-w-2xl">
          <p className="text-lg leading-relaxed text-zinc-200 sm:text-xl">We combine generative AI with human creative direction to create complete, brand-ready video productions.</p>
          <p className="mt-4 text-base leading-relaxed text-zinc-400">From brand films and advertising campaigns to product videos, explainers and social content, we take your idea from concept to finished video.</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Pill label="View Our Work" variant="outline" onClick={() => scrollToId("work")} testid="studio-hero-work" />
            <Pill label="Start a Project" testid="studio-hero-start" />
          </div>
        </motion.div>
      </motion.div>

      <button type="button" onClick={toggle} data-testid="studio-sound-toggle" aria-label={muted ? "Unmute" : "Mute"}
        className="absolute bottom-6 right-5 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-sink/40 text-white backdrop-blur-md transition-colors duration-300 hover:border-brand hover:text-brand sm:bottom-8 sm:right-8 lg:right-12">
        {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </button>
      <div className="pointer-events-none absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex">
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-400">Scroll</span>
        <span className="relative h-12 w-px overflow-hidden bg-white/15"><span className="studio-scroll-line absolute inset-x-0 top-0 h-1/2 bg-brand" /></span>
      </div>
    </section>
  );
};
